// Briefing chat — a compact text chat with the reader's Merlin on the Briefing
// page (the profile-LOCKED /api/person-digest/chat door). Since kanban 001b6e2b
// everything Merlin writes for a reader is a WATCH (the daily briefing is one),
// and this chat is how they change them ("add a line per CSR", "watch October
// sales every 48 hours", "we're done with this one"): Merlin's watch tools write
// the same rows the buttons do. What is true of the reader across all their
// watches ("numbers before prose") is recorded into their memory bank. In
// `watch` mode the chat sits beside a watch's readout and sends that watch as
// the turn's `focus`, so "this one" needs no explaining; the parent refreshes
// its view when a turn completes.
//
// Reuses the Agent Console transcript renderer (AssistantTurn), the Analyst's
// pure turn reducer, and the shared SSE wire contract. No history drawer, no
// model picker, no attachments: one focused thread per visit (a refresh
// resumes it via the per-user store).

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AgentFileCard } from '../agentConsole/AgentFileCard';
import { AssistantTurn } from '../agentConsole/AssistantTurnView';
import { MessageTimestamp } from '../agentConsole/MessageTimestamp';
import { makeActiveConvStore } from '../agentConsole/conversationHistory';
import { useAuth } from '../../context/AuthContext';
import {
  detailToAnalystTurns,
  reduceTurn,
  type AssistantTurnModel,
  type Turn,
} from '../../pages/analystTurns';
import { turnSetupErrorMessage } from '../../services/agentConsole';
import {
  PERSON_DIGEST_CHAT_BASE,
  createDigestConversation,
  getDigestConversationDetail,
  streamDigestMessage,
} from '../../services/personDigest';
import styles from './DigestChat.module.css';

const activeConvStore = makeActiveConvStore('cm_person_digest_convid_');

/** `general`: no watch selected; `watch`: beside one watch's readout. */
export type DigestChatMode = 'general' | 'watch';

export interface DigestChatProps {
  /** The reader's first name, for the intro copy. */
  readerName: string;
  /** Called after each turn finishes (success or error) — a remembered ask or
   * a watch change may have landed, so the parent re-reads its view. */
  onTurnComplete?: () => void;
  /** `watch`: the chat sits beside one watch's readout. */
  mode?: DigestChatMode;
  /** The watch beside the chat (watch mode): sent as the turn's focus. */
  watch?: { id: string; subject: string } | null;
}

function clip(text: string, max = 90): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function DigestChat({ readerName, onTurnComplete, mode = 'general', watch = null }: DigestChatProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const convIdRef = useRef<string | null>(null);
  const idCounter = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef(false);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const watchMode = mode === 'watch' && watch !== null;

  const nextId = () => `t${idCounter.current++}`;

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const patchAssistant = useCallback(
    (id: string, fn: (t: AssistantTurnModel) => AssistantTurnModel) => {
      setTurns((prev) => prev.map((t) => (t.id === id && t.role === 'assistant' ? fn(t) : t)));
    },
    [],
  );

  // Resume the reader's last thread on mount (per-user remembered id).
  useEffect(() => {
    if (restoredRef.current || !userId) return;
    restoredRef.current = true;
    const resumeId = activeConvStore.read(userId);
    if (!resumeId) return;
    convIdRef.current = resumeId;
    getDigestConversationDetail(resumeId)
      .then((detail) => setTurns(detailToAnalystTurns(detail)))
      .catch(() => {
        // A thread that no longer resolves: start fresh silently.
        convIdRef.current = null;
        activeConvStore.write(userId, null);
      });
  }, [userId]);

  const send = useCallback(
    async (raw: string) => {
      const prompt = raw.trim();
      if (!prompt || sending) return;
      setError(null);
      setSending(true);
      const asstId = nextId();
      const sentAt = new Date().toISOString();
      setTurns((prev) => [
        ...prev,
        { id: nextId(), role: 'user', text: prompt, createdAt: sentAt },
        { id: asstId, role: 'assistant', blocks: [], streaming: true, createdAt: sentAt },
      ]);
      try {
        let cid = convIdRef.current;
        if (!cid) {
          const convo = await createDigestConversation({ title: `Briefing chat — ${readerName}` });
          cid = convo.id;
          convIdRef.current = cid;
          activeConvStore.write(userId, cid);
        }
        const focus = watchMode && watch ? { watch_id: watch.id } : undefined;
        await streamDigestMessage(
          cid,
          prompt,
          (evt) => {
            if (evt.type === 'error' || evt.type === 'cancelled') {
              setError(evt.message || 'Something went wrong — please try again.');
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            if (evt.type === 'state' || evt.type === 'done') {
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            patchAssistant(asstId, (t) => ({ ...t, ...reduceTurn(t, evt) }));
          },
          undefined,
          focus,
        );
      } catch (e) {
        setError(turnSetupErrorMessage(e, 'Could not reach Merlin.'));
      } finally {
        patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
        setSending(false);
        onTurnComplete?.();
      }
    },
    [sending, patchAssistant, readerName, userId, onTurnComplete, watchMode, watch],
  );

  const submitText = useCallback(() => {
    const v = input;
    setInput('');
    void send(v);
  }, [input, send]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitText();
    }
  };

  const startNew = () => {
    if (sending) return;
    convIdRef.current = null;
    activeConvStore.write(userId, null);
    setTurns([]);
    setError(null);
  };

  return (
    <section
      className={styles.chat}
      aria-label={watchMode ? 'Tell Merlin about this watch' : 'Tell Merlin about your watches'}
    >
      <header className={styles.head}>
        <span className={styles.dot} aria-hidden="true" />
        <div className={styles.headTitle}>
          <span className={styles.title}>{watchMode ? 'Tell Merlin about this watch' : 'Tell Merlin'}</span>
          <span className={styles.meta}>
            {watchMode && watch
              ? clip(watch.subject)
              : 'Ask about the data, say what to watch, or how you like to read'}
          </span>
        </div>
        <button
          type="button"
          className={styles.newBtn}
          onClick={startNew}
          disabled={sending}
          data-action="digest_chat_new"
          data-outcome="none"
        >
          New chat
        </button>
      </header>

      <div className={styles.log} ref={logRef}>
        {turns.length === 0 && (
          <div className={styles.intro}>
            <div className={styles.introTitle}>Hi {readerName} 👋</div>
            {watchMode && watch ? (
              <>
                You are looking at the readout for “{clip(watch.subject, 120)}”. Tell me what it
                should show instead or as well — I rewrite it and keep honouring that on every
                later check:
                <ul className={styles.examples}>
                  <li>“Show the example responses, not just the counts.”</li>
                  <li>“Make it shorter.”</li>
                  <li>“Check it every 48 hours.”</li>
                  <li>“We’re done with this one.”</li>
                </ul>
              </>
            ) : (
              <>
                Ask me about the data, tell me what to watch and how often, or how you like to
                read — I remember that for every watch:
                <ul className={styles.examples}>
                  <li>“Watch whether October picks up on the sales site, every 48 hours.”</li>
                  <li>“Start my daily briefing on the quality signals.”</li>
                  <li>“Always put the numbers before the prose.”</li>
                </ul>
              </>
            )}
          </div>
        )}
        {turns.map((t) =>
          t.role === 'user' ? (
            <div key={t.id} className={styles.userWrap}>
              <div className={styles.userMsg}>{t.text}</div>
              {t.createdAt ? (
                <div className={styles.userCap}>
                  <MessageTimestamp at={t.createdAt} />
                </div>
              ) : null}
            </div>
          ) : (
            <div key={t.id} className={styles.asstWrap}>
              {(t.blocks.length > 0 || t.streaming) && (
                <div className={styles.asstMsg}>
                  <AssistantTurn blocks={t.blocks} isStreaming={t.streaming} />
                  {t.streaming && t.blocks.length === 0 && (
                    <span className={styles.thinking}>Looking things up…</span>
                  )}
                </div>
              )}
              {t.agentFiles?.map((file) => (
                <AgentFileCard
                  key={file.attachmentId}
                  base={PERSON_DIGEST_CHAT_BASE}
                  file={file}
                  conversationId={convIdRef.current}
                />
              ))}
            </div>
          ),
        )}
      </div>

      {error && (
        <div className={styles.errorBar} role="alert">
          {error}
        </div>
      )}

      <div className={styles.composer}>
        <textarea
          className={styles.textarea}
          placeholder={watchMode ? 'No, I want to see…' : 'Ask, or say what to watch…'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          aria-label="Message Merlin"
        />
        <button
          type="button"
          className={styles.sendBtn}
          onClick={submitText}
          disabled={sending || !input.trim()}
          data-action="digest_chat_send"
          data-target-watch-id={watchMode && watch ? watch.id : undefined}
          data-outcome="request"
        >
          Send
        </button>
      </div>
    </section>
  );
}
